import { useRef, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { CATEGORIES, CATEGORY_COLORS, todayISO } from "./expenseUtils";

export const ExpenseForm = ({ onAdd }) => {
  const { t } = useLang();
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("materials");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const amountRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      toast.error(t("exp_invalid_amount"));
      amountRef.current?.focus();
      return;
    }
    setSaving(true);
    const ok = await onAdd({ amount: value, category, date, note: note.trim() || null });
    setSaving(false);
    if (ok) {
      setAmount("");
      setNote("");
      amountRef.current?.focus();
    }
  };

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-2 md:grid-cols-[150px_180px_170px_1fr_auto] gap-3 items-center"
      data-testid="expense-form"
    >
      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-display font-bold text-[#F26522]">
          $
        </span>
        <input
          ref={amountRef}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={t("exp_amount_ph")}
          className="field-input field-input-dark w-full !pl-8 font-display font-bold text-lg tabular-nums"
          data-testid="expense-amount-input"
        />
      </div>
      <div className="relative">
        <span
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: CATEGORY_COLORS[category] }}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="field-input field-input-dark w-full !pl-9 cursor-pointer"
          data-testid="expense-category-select"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`cat_${c}`)}
            </option>
          ))}
        </select>
      </div>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="field-input field-input-dark w-full [color-scheme:dark]"
        data-testid="expense-date-input"
      />
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("exp_note_ph")}
        maxLength={300}
        className="field-input field-input-dark w-full"
        data-testid="expense-note-input"
      />
      <button
        type="submit"
        disabled={saving}
        className="col-span-2 md:col-span-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-6 py-3 text-sm transition-[background-color,transform] duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60 whitespace-nowrap"
        data-testid="expense-add-btn"
      >
        <Plus className="h-4 w-4" />
        {saving ? t("exp_adding") : t("exp_add")}
      </button>
    </form>
  );
};
